/**
 * TOPO — Topografie rulebook.
 *
 * Sourced from DEFAULT_POLICY (Topo entries — note: moduleRuleRefRequired=false
 * because topografie is knowledge-recall, not rule-based) +
 * MODULE_AUTHORING_GUIDE §10.
 *
 * v2 (mei 2026) — uitgebreid met per-region en per-vraagtype hints.
 * Achtergrond: alleen TOPO-NL is volledig uitgewerkt met map_locate_*
 * vraagtypes + topoMap-stimuli. TOPO-EU en TOPO-WERELD bevatten nog
 * grotendeels pure MC zonder kaart. Deze hints stellen de richting vast
 * waarop EU/Wereld uitgebouwd moeten worden, en zorgen dat bestaande NL
 * seeds compliant blijven.
 */

import type { ModuleRulebook } from './types'

export const TOPO_RULEBOOK: ModuleRulebook = {
  moduleId: 'Topo',
  shortCode: 'TOPO',
  displayName: 'Topografie',

  didactics: {
    principles: [
      'kennisreproductie',
      'kaart_als_primair',
      'positionele_redenering',
      'klimaat_en_landschap',
    ],
    schoolbookReferences: ['Topondernemers', 'De Blauwe Planeet'],
    misconceptionDomain: 'topo',
    coreSummary:
      'Topografische kennis: locaties, hoofdsteden, grenzen, rivieren, gebergten, klimaat en landschapstypen.',
  },

  notation: {},

  content: {
    distractorMisconceptionPattern:
      'provincie|hoofdstad|rivier|gebergte|oceaan|continent|zee|eiland|grens|stad|berg|kust|noorden|zuiden|oosten|westen|stroomt|ligt|grenst',
  },

  explanation: {
    // Topo is knowledge-recall — no formal "rule" to reference. Explanations
    // still need to be didactic (where? hoe herken je het?) but the rule-ref
    // check is off.
    requireRuleReference: false,
    checkStrategyMinTier: null,
    preferredStrategyKeywords: [
      'ligging',
      'grenst aan',
      'stroomt door',
      'hoofdstad',
    ],
  },

  visuals: {
    preferredAidTypes: ['topoMap', 'referenceArrow', 'rule'],
    familyVisualHints: [
      {
        familyIdPattern: '^TOPO-NL-PROV|^TOPO-NL-EILANDEN|^TOPO-NL-STEDEN',
        preferredStimulus: 'topoMap',
        note: 'NL locatie-vragen: kaart is verplichte stimulus; vraagtypen map_locate_region/city/point waar mogelijk.',
      },
      {
        familyIdPattern: '^TOPO-NL-STEDEN-REGIO',
        preferredStimulus: 'topoMap',
        note: 'Stad-binnen-regio: kaart toont provincie met steden gemarkeerd.',
      },
      {
        familyIdPattern: '^TOPO-NL-BUURLANDEN|^TOPO-EU-BUURLANDEN',
        preferredStimulus: 'topoMap',
        preferredSupport: 'referenceArrow',
        note: 'Buurlanden: kaart met pijl/markering naar het buurland — referenceArrow als support voor "grenst aan".',
      },
      {
        familyIdPattern: '^TOPO-NL-WATER|^TOPO-EU-RIVIEREN|^TOPO-WERELD-RIVIER',
        preferredStimulus: 'topoMap',
        note: 'Rivieren als lijn-features op de kaart; vraag verwijst naar de gemarkeerde route.',
      },
      {
        familyIdPattern: '^TOPO-EU-ZEEEN|^TOPO-WERELD-OCEANEN',
        preferredStimulus: 'topoMap',
        note: 'Zeeën en oceanen: kaart met blauw gemarkeerde wateroppervlakken.',
      },
      {
        familyIdPattern: '^TOPO-EU-GEBERGTE|^TOPO-WERELD-GEBERGTE',
        preferredStimulus: 'topoMap',
        note: 'Gebergten als region-highlight op de kaart.',
      },
      {
        familyIdPattern: '^TOPO-EU-HOOFDST|^TOPO-WERELD-HOOFDST',
        preferredSupport: 'topoMap',
        note: 'Hoofdsteden zijn kennis-recall; kaart toont positie als support bij wrong-answer.',
      },
      {
        familyIdPattern: '^TOPO-EU-LAND|^TOPO-WERELD-LANDEN|^TOPO-WERELD-CONT',
        preferredStimulus: 'topoMap',
        note: 'Landen/continenten herkennen op de kaart — kaart als stimulus, niet alleen lijst-MC.',
      },
      {
        familyIdPattern: '^TOPO-KLIMAAT|^TOPO-EU-KLIMAAT|^TOPO-WERELD-KLIMAAT',
        preferredStimulus: 'topoMap',
        preferredSupport: 'rule',
        note: 'Klimaatzones: choropleth-achtige kaart-highlight + regel-kaart met klimaattypes.',
      },
      {
        familyIdPattern: '^TOPO-NL-LANDSCHAP|^TOPO-EU-FYSISCH',
        preferredStimulus: 'topoMap',
        note: 'Landschapstypen / fysische geografie: kaart als primair venster.',
      },
      {
        familyIdPattern: '^TOPO-',
        preferredSupport: 'topoMap',
        note: 'Topo-default: indien geen kaart als stimulus, dan minimaal als support voor wrong-answer.',
      },
    ],
  },

  questionTypes: {
    preferredTypes: [
      'multiple_choice',
      'map_locate_region',
      'map_locate_point',
      'map_locate_line',
      'map_place_cities',
      'true_false_series',
      'matching',
    ],
    familyTypeHints: [
      {
        familyIdPattern: '^TOPO-(NL|EU|WERELD)-(PROV|LAND|HOOFDST)',
        preferredTypes: ['map_locate_region', 'multiple_choice', 'matching'],
        note: 'Provincies/landen/hoofdsteden: locatie op kaart aanwijzen of MC — kennis-recall met kaart als anker.',
      },
      {
        familyIdPattern: '^TOPO-.*-TF',
        preferredTypes: ['true_false_series', 'multiple_choice'],
        note: 'TF-varianten op topo-stellingen: ligging-uitspraken beoordelen — herkenning is hier didactisch passend.',
      },
      {
        familyIdPattern: '^TOPO-NL-STEDEN(-REGIO)?',
        preferredTypes: ['map_locate_point', 'multiple_choice', 'matching'],
        note: 'NL-steden: kaart-aanwijzing op stedenkaart of regio-koppeling via matching.',
      },
      {
        familyIdPattern: '^TOPO-NL-(WATER|EILANDEN|LANDSCHAP)',
        preferredTypes: [
          'map_locate_region',
          'map_locate_point',
          'map_locate_line',
          'multiple_choice',
          'matching',
        ],
        note: 'NL water/eilanden/landschap: kaart-renderer voor ligging — rivieren via map_locate_line, stad-water via matching.',
      },
      {
        familyIdPattern: '^TOPO-NL-RIVIEREN',
        preferredTypes: ['map_locate_line', 'multiple_choice', 'matching'],
        note: 'NL-rivieren: klikken op de rivier-polyline is het primaire didactische type; MC en matching voor herkenning op naam.',
      },
      {
        familyIdPattern: '^TOPO-(NL|EU|WERELD)-BUURLANDEN',
        preferredTypes: ['map_locate_region', 'multiple_choice', 'matching'],
        discouragedTypes: ['true_false_series'],
        note: 'Buurland-relaties: regio aanwijzen of matchen — buurland-trap-distractor werkt het best in MC/match.',
      },
      {
        familyIdPattern: '^TOPO-EU-(GEBERGTE|RIVIEREN|ZEEEN|FYSISCH|KLIMAAT)',
        preferredTypes: [
          'map_locate_region',
          'map_locate_point',
          'multiple_choice',
          'matching',
        ],
        note: 'Fysisch-geografische features: locatie op kaart of MC — bergen/rivieren/zeeen zijn ruimtelijk.',
      },
      {
        familyIdPattern:
          '^TOPO-WERELD-(CONT|OCEANEN|GEBERGTE|RIVIER|KLIMAAT|LANDEN)',
        preferredTypes: ['map_locate_region', 'multiple_choice', 'matching'],
        note: 'Wereld-niveau topo: kaart-aanwijzing of MC — matching voor categorisatie (continent-land).',
      },
      {
        familyIdPattern: '^TOPO-KLIMAAT',
        preferredTypes: ['multiple_choice', 'matching', 'map_locate_region'],
        note: 'Klimaatzones: classificatie via MC/matching of regio-aanwijzing op klimaatkaart.',
      },
    ],
  },

  distractorTraps: [
    {
      id: 'neighbor-country',
      label: 'Buurland/buurprovincie',
      description:
        'Antwoord met een aangrenzende provincie/land — kerntrap voor topo.',
    },
    {
      id: 'capital-vs-largest',
      label: 'Hoofdstad vs. grootste stad',
      description:
        'Grootste stad noemen waar de hoofdstad gevraagd wordt (Amsterdam vs. Den Haag).',
    },
  ],

  keyRules: [
    'Kaart-visual verplicht voor locatievragen (NL volledig; EU/Wereld in opbouw)',
    'Trap-distractor: aangrenzend land/provincie',
    'Hoofdstad vs. grootste stad onderscheid vermelden',
    'Geen formele "regels" — uitleg gaat over ligging en context',
    'Hint-volgorde: specifieke regio-patronen vóór de generieke ^TOPO- fallback',
  ],

  authoringGuideAnchor: '#10-topo--topografie',
}
